import { DatePicker, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@tellescope/ui";
import { TellescopeFormFieldBaseProps } from "../types";
import { useForm } from "../../use-form";
import { useTranslation } from "react-i18next";
import { INSURANCE_RELATIONSHIPS } from "@tellescope/constants";
import { InsuranceRelationship } from "@tellescope/types-models";
import { cn } from "@tellescope/utils";

const convertStringToInsuranceRelationship = (str: string): InsuranceRelationship | undefined => {
  return INSURANCE_RELATIONSHIPS.find((item) => item === str);
};

export interface InsuranceFieldProps extends TellescopeFormFieldBaseProps<"Insurance"> {}

export const InsuranceField = ({ title, value, onChange, className, onBlur, error }: InsuranceFieldProps) => {
  const { t } = useTranslation("forms");
  const form = useForm({
    fields: {
      insurer: {
        type: "text",
        initialValue: value?.payerName ?? "",
      },
      memberId: {
        type: "text",
        initialValue: value?.memberId ?? "",
      },
      planName: {
        type: "text",
        initialValue: value?.planName ?? "",
      },
      relationship: {
        type: "select",
        initialValue: value?.relationship ?? "",
      },
      startDate: {
        type: "date",
        initialValue: value?.startDate,
      },
    },
    sideEffects: (newValues) => {
      onChange({
        ...newValues,
        relationship: convertStringToInsuranceRelationship(newValues.relationship),
        startDate: newValues?.startDate?.toISOString(),
      });
      return newValues;
    },
    validateOn: "blur",
  });

  const getFieldProps: typeof form.getFieldProps = (fieldName) => {
    const props = form.getFieldProps(fieldName);
    return {
      ...props,
      onBlur: () => {
        props.onBlur();
        onBlur?.();
      },
    };
  };

  const getCustomFieldProps: typeof form.getCustomFieldProps = (fieldName) => {
    const props = form.getCustomFieldProps(fieldName);
    return {
      ...props,
      onBlur: () => {
        props.onBlur();
        onBlur?.();
      },
    };
  };

  return (
    <div className="space-y-1">
      {!!title && <h4>{title}</h4>}
      <div className="grid grid-cols-2 gap-2">
        <Input {...getFieldProps("insurer")} placeholder={t("Insurer")} />
        <Input {...getFieldProps("memberId")} placeholder={t("Member ID")} />
        <Input {...getFieldProps("planName")} placeholder={t("Plan Name")} />
        <DatePicker
          {...getCustomFieldProps("startDate")}
          placeholder={t("Plan Start Date")}
          label=""
          className="w-full"
        />
        <Select {...getCustomFieldProps("relationship")}>
          <SelectTrigger className={cn("col-span-2", className)}>
            <SelectValue placeholder={t("Relationship to Policy Owner")}>{form.values.relationship}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {INSURANCE_RELATIONSHIPS.map((relationship) => (
              <SelectItem key={relationship} value={relationship}>
                {relationship}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
